// ═══════════════════════════════════════════════════════════════
// RollCall — Dashboard
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePolling } from '../../hooks/usePolling';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, SimpleGrid,
} from '@mantine/core';
import {
  IconLogin,
  IconPlus, IconArrowRight, IconDeviceDesktop,
} from '@tabler/icons-react';
import { useAuthStore } from '../../store/authStore';
import { useRollCallStore } from '../../store/rollcallStore';
import { RC_EVENT_TYPE_META } from '../../types';
import { primary } from '../../lib/colors';

export default function RollCallDashboard() {
  const navigate = useNavigate();
  const { incidents, loadIncidents } = useAuthStore();
  const { personnel, events, checkins, loadAll } = useRollCallStore();

  useEffect(() => { loadIncidents(); loadAll(); }, []);
  usePolling(() => { loadAll(); });

  const openEvents = useMemo(() => events.filter(e => e.eventStatus === 'open'), [events]);
  const currentlyIn = useMemo(
    () => checkins.filter(c => !c.checkOutTime),
    [checkins]
  );
  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">RollCall Dashboard</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconPlus size={14} />}
            onClick={() => navigate('/rollcall/events/new')}>New Event</Button>
          <Button size="xs" variant="light" color="teal" leftSection={<IconLogin size={14} />}
            onClick={() => navigate('/rollcall/checkin')}>Check-In</Button>
          <Button size="xs" variant="light" color="grape" leftSection={<IconDeviceDesktop size={14} />}
            onClick={() => navigate('/rollcall/kiosk')}>Kiosk Mode</Button>
        </Group>
      </Group>

      {/* Stat Cards */}
      <SimpleGrid cols={{ base: 2, sm: 5 }} mb="md" spacing="sm">
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: `3px solid ${primary[700]}` }}>
          <Text size="xs" c="dimmed">Currently In</Text>
          <Text fz={28} fw={700}>{currentlyIn.length}</Text>
        </Paper>
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: `3px solid ${primary[900]}` }}>
          <Text size="xs" c="dimmed">Personnel</Text>
          <Text fz={28} fw={700}>{personnel.filter(p => p.status === 'active').length}</Text>
        </Paper>
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: '3px solid #07963F' }}>
          <Text size="xs" c="dimmed">Open Events</Text>
          <Text fz={28} fw={700} c="#07963F">{openEvents.length}</Text>
        </Paper>
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: `3px solid ${primary[700]}` }}>
          <Text size="xs" c="dimmed">Total Check-ins</Text>
          <Text fz={28} fw={700}>{checkins.length}</Text>
        </Paper>
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: '3px solid #A67103' }}>
          <Text size="xs" c="dimmed">Avg per Event</Text>
          <Text fz={28} fw={700} c="#A67103">{events.length > 0 ? Math.round(checkins.length / events.length) : 0}</Text>
        </Paper>
      </SimpleGrid>

      {/* Active Events (full width) */}
      <Paper p="sm" radius="sm" withBorder>
        <Group justify="space-between" mb="sm">
          <Text fw={600} size="sm">Active Events</Text>
          <Badge size="sm" variant="light" color="green">{openEvents.length}</Badge>
        </Group>
        {openEvents.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">No active events.</Text>
        ) : (
          <Stack gap="xs">
            {openEvents.slice(0, 10).map(ev => {
              const inc = incidents.find(i => String(i.id) === String(ev.incidentId));
              const evCheckins = checkins.filter(c => String(c.eventId) === String(ev.id) && !c.checkOutTime);
              const meta = RC_EVENT_TYPE_META[ev.eventType] || RC_EVENT_TYPE_META.custom;
              return (
                <Paper key={ev.id} p="xs" radius="sm" withBorder
                  style={{ cursor: 'pointer' }} onClick={() => navigate(`/rollcall/event/${ev.id}`)}>
                  <Group justify="space-between">
                    <div>
                      <Group gap="xs" mb={2}>
                        <Text size="sm" fw={600}>{ev.name}</Text>
                        <Badge size="xs" color={meta.color}>{meta.label}</Badge>
                      </Group>
                      <Text size="xs" c="dimmed">
                        {inc?.name || '—'} · {ev.eventDate || '—'}
                      </Text>
                    </div>
                    <Badge size="sm" variant="light" color="blue">{evCheckins.length} in</Badge>
                  </Group>
                </Paper>
              );
            })}
          </Stack>
        )}
        {openEvents.length > 0 && (
          <Button variant="subtle" size="xs" mt="sm" fullWidth
            rightSection={<IconArrowRight size={14} />}
            onClick={() => navigate('/rollcall/events')}>View All Events</Button>
        )}
      </Paper>
    </div>
  );
}
